import { cn } from "@/lib/utils";
import { isHTML } from "@/lib/legacyToHtml";
import HyperlinkRenderer from "@/components/HyperlinkRenderer";

interface RichContentRendererProps {
  content: string;
  className?: string;
  emptyText?: string;
}

/**
 * View-only renderer that handles both legacy string format and HTML content.
 * - HTML (from TipTap): rendered via dangerouslySetInnerHTML
 * - Legacy format ([text](url), @[name](id)): rendered via HyperlinkRenderer
 */
const RichContentRenderer = ({ content, className, emptyText = "—" }: RichContentRendererProps) => {
  const isEmpty = !content || content === "<p></p>" || content === "<p><br></p>";

  if (isEmpty) {
    return <span className={cn("text-muted-foreground/70", className)}>{emptyText}</span>;
  }

  if (isHTML(content)) {
    return (
      <div
        className={cn(
          "text-sm text-foreground",
          // Render paragraph breaks (Enter) and hard breaks (Shift+Enter) the
          // same: no extra inter-paragraph gap. Empty paragraphs still show a
          // blank line via the nbsp ::before rules below.
          "[&_p]:my-0",
          "[&_p:empty]:before:content-['\\00a0']",
          "[&_p:has(>br:only-child)]:before:content-['\\00a0']",
          "[&_p:has(>br:only-child)>br]:hidden",
          "[&_ul]:list-disc [&_ul]:pl-4 [&_ul]:my-1",
          "[&_ol]:list-decimal [&_ol]:pl-4 [&_ol]:my-1",
          "[&_li]:my-0.5",
          "[&_strong]:font-semibold",
          "[&_em]:italic",
          "[&_s]:line-through",
          "[&_a]:text-blue-600 [&_a]:underline [&_a]:hover:text-blue-800",
          "[&_pre]:bg-zinc-900 [&_pre]:text-zinc-100 [&_pre]:rounded-md [&_pre]:p-3 [&_pre]:my-2 [&_pre]:text-sm [&_pre]:font-mono [&_pre]:overflow-x-auto",
          "[&_pre_code]:bg-transparent [&_pre_code]:text-inherit [&_pre_code]:p-0",
          "[&_code]:bg-muted [&_code]:text-foreground [&_code]:rounded [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-sm [&_code]:font-mono",
          "[&_mark]:bg-yellow-200 [&_mark]:rounded [&_mark]:px-0.5",
          "[&_[data-type='mention']]:bg-primary/10 [&_[data-type='mention']]:text-brand [&_[data-type='mention']]:font-medium [&_[data-type='mention']]:px-1 [&_[data-type='mention']]:rounded",
          className,
        )}
        dangerouslySetInnerHTML={{ __html: content }}
      />
    );
  }

  return (
    <p className={cn("text-foreground whitespace-pre-wrap text-sm", className)}>
      <HyperlinkRenderer text={content} />
    </p>
  );
};

export default RichContentRenderer;
